import React from 'react';
import PropTypes from 'prop-types';
import { Badge, IconButton, Tooltip } from '@mui/material';
import { FilterList } from '@mui/icons-material';

/**
 * FilterToggleButton — botão padrão do navbar pra abrir o `FilterDrawer`.
 *
 * Visual coerente com os outros botões do navbar (quadrado translúcido azul +
 * border-radius 10px) e com estado VISUAL DE ATIVO quando há filtros:
 *   - Estado normal: bg `rgba(0,128,173,0.08)` + ícone primary
 *   - Estado ativo (`activeCount > 0`): bg primary cheio + ícone branco +
 *     Badge numérico + shadow azul
 *
 * Substitui o IconButton "solto" que cada tela criava — agora todas usam o
 * mesmo padrão (mesma cor, mesmo tamanho, mesmo comportamento).
 */
function FilterToggleButton(props) {
  const { onClick, activeCount = 0, tooltip = 'Filtrar', size = 'large', sx } = props;
  const isActive = activeCount > 0;
  const activeTooltip =
    isActive && tooltip === 'Filtrar'
      ? `${activeCount} filtro${activeCount > 1 ? 's' : ''} ativo${activeCount > 1 ? 's' : ''}`
      : tooltip;

  return (
    <Tooltip title={activeTooltip}>
      <Badge
        badgeContent={activeCount}
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiBadge-badge': {
            bgcolor: '#0080ad',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.65rem',
            minWidth: 18,
            height: 18,
            border: '2px solid #fff',
            boxShadow: '0 2px 6px rgba(0, 128, 173, 0.35)',
            top: 6,
            right: 6,
          },
        }}
      >
        <IconButton
          onClick={onClick}
          size={size}
          aria-label="Filtrar"
          sx={{
            color: isActive ? '#fff' : '#0080ad',
            bgcolor: isActive ? '#0080ad' : 'rgba(0, 128, 173, 0.08)',
            borderRadius: '10px',
            p: 1,
            ml: 0.5,
            boxShadow: isActive ? '0 6px 16px rgba(0, 128, 173, 0.32)' : 'none',
            transition:
              'background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease',
            '&:hover': {
              bgcolor: isActive ? '#197997' : 'rgba(0, 128, 173, 0.14)',
            },
            ...sx,
          }}
        >
          <FilterList />
        </IconButton>
      </Badge>
    </Tooltip>
  );
}

FilterToggleButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  activeCount: PropTypes.number,
  tooltip: PropTypes.string,
  size: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  sx: PropTypes.object,
};

export default FilterToggleButton;
